import ResolvedGlossaryPanel from './components/ResolvedGlossaryPanel';
import { getAuthHeaders } from "./utils/authHeaders";
// GlossaryRuleModal.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { bookDetailApiPost } from './bookDetail/BookDetailPage.api';
import { Modal, Input, Button, Tag, Tooltip, message, Dropdown, Menu } from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  QuestionCircleOutlined,
  PlusOutlined,
  CopyOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { normalizeBookTermLists, resolveSeriesTerms, resolveBookTerms } from './utils/glossaryKeys';
import { isValidGlossaryTerm } from './utils/glossaryValidation';
import { splitLanguagePair } from './utils/languagePairs';
import LoadingCard from './components/LoadingCard';
import { isTutorialModeRequested, isTutorialSandboxModeEnabled } from './utils/tutorialMode';
import './GlossaryRuleModal.css';

type RuleType = 'always' | 'never';
type SortField = 'source' | 'target';
type SortDirection = 'asc' | 'desc';
type SortState = {
  field: SortField | null;
  direction: SortDirection | null;
};

interface BaseGlossaryRuleModalProps {
  seriesGlossaryStatus?: import('./hooks/useSeriesGlossary').SeriesGlossaryStatus;
  onRefreshSeriesGlossary?: () => void;
  isVisible: boolean;
  onClose: () => void;
  currentLanguagePair: string;
  bookGuid: string;
  selectedSeriesId?: string | null;
  resolvedUserId?: string;
  apiBase?: string;
  setSeriesTranslateRules?: React.Dispatch<
    React.SetStateAction<{
      alwaysTranslate: Record<string, string[]>;
      neverTranslate: Record<string, string[]>;
    }>
  >;
  onSwitchRuleType?: (nextRuleType: RuleType) => void;
}

interface AlwaysGlossaryRuleModalProps extends BaseGlossaryRuleModalProps {
  ruleType: 'always';
  alwaysTranslateWords: { [key: string]: string[] };
  seriesAlwaysTranslateWords?: { [key: string]: string[] };
  neverTranslateWords?: { [key: string]: string[] };
  seriesNeverTranslateWords?: { [key: string]: string[] };
  setAlwaysTranslateWords: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
}

interface NeverGlossaryRuleModalProps extends BaseGlossaryRuleModalProps {
  ruleType: 'never';
  neverTranslateWords: { [key: string]: string[] };
  seriesNeverTranslateWords?: { [key: string]: string[] };
  alwaysTranslateWords?: { [key: string]: string[] };
  seriesAlwaysTranslateWords?: { [key: string]: string[] };
  setNeverTranslateWords: React.Dispatch<React.SetStateAction<{ [key: string]: string[] }>>;
}

type GlossaryRuleModalProps = AlwaysGlossaryRuleModalProps | NeverGlossaryRuleModalProps;

type GlossaryPairEntry = {
  source: string;
  target: string;
  scope: 'book' | 'series';
  bookIndex?: number;
  raw: string;
};

type GlossaryTermEntry = {
  value: string;
  scope: 'book' | 'series';
  raw: string;
};

const parseLanguagePair = (pair: string) => {
  const { sourceLang, targetLang } = splitLanguagePair(pair);
  return { sourceLabel: sourceLang, targetLabel: targetLang };
};

const GlossaryRuleModal: React.FC<GlossaryRuleModalProps> = (props) => {
  const {
    isVisible,
    onClose,
    currentLanguagePair,
    bookGuid,
    ruleType,
    onSwitchRuleType,
    selectedSeriesId,
    resolvedUserId,
    apiBase,
    setSeriesTranslateRules,
  } = props;
  const isAlways = ruleType === 'always';
  const { sourceLabel, targetLabel } = parseLanguagePair(currentLanguagePair);
  const { sourceLang, targetLang } = splitLanguagePair(currentLanguagePair);
  const API_BASE = apiBase || 'https://askpoeai-nswdzfky7q-uc.a.run.app';
  const tutorialModeRequested = isTutorialModeRequested() || isTutorialSandboxModeEnabled();

  let alwaysTranslateWords: Record<string, string[]> = {};
  let seriesAlwaysTranslateWords: Record<string, string[]> = {};
  let setAlwaysTranslateWords: React.Dispatch<React.SetStateAction<Record<string, string[]>>> | null = null;
  let neverTranslateWords: Record<string, string[]> = {};
  let seriesNeverTranslateWords: Record<string, string[]> = {};
  let setNeverTranslateWords: React.Dispatch<React.SetStateAction<Record<string, string[]>>> | null = null;
  let conflictAlwaysTranslateWords: Record<string, string[]> = {};
  let conflictSeriesAlwaysTranslateWords: Record<string, string[]> = {};
  let conflictNeverTranslateWords: Record<string, string[]> = {};
  let conflictSeriesNeverTranslateWords: Record<string, string[]> = {};

  if (isAlways) {
    const alwaysProps = props as AlwaysGlossaryRuleModalProps;
    alwaysTranslateWords = alwaysProps.alwaysTranslateWords;
    seriesAlwaysTranslateWords = alwaysProps.seriesAlwaysTranslateWords ?? {};
    setAlwaysTranslateWords = alwaysProps.setAlwaysTranslateWords;
    conflictNeverTranslateWords = alwaysProps.neverTranslateWords ?? {};
    conflictSeriesNeverTranslateWords = alwaysProps.seriesNeverTranslateWords ?? {};
  } else {
    const neverProps = props as NeverGlossaryRuleModalProps;
    neverTranslateWords = neverProps.neverTranslateWords;
    seriesNeverTranslateWords = neverProps.seriesNeverTranslateWords ?? {};
    setNeverTranslateWords = neverProps.setNeverTranslateWords;
    conflictAlwaysTranslateWords = neverProps.alwaysTranslateWords ?? {};
    conflictSeriesAlwaysTranslateWords = neverProps.seriesAlwaysTranslateWords ?? {};
  }

  const [newSourceWord, setNewSourceWord] = useState('');
  const [newTargetWord, setNewTargetWord] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedSourceWord, setEditedSourceWord] = useState('');
  const [editedTargetWord, setEditedTargetWord] = useState('');
  const [newWord, setNewWord] = useState('');
  const [editingWord, setEditingWord] = useState<string | null>(null);
  const [editedWord, setEditedWord] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [contractRefresh, setContractRefresh] = useState(0);
  const [contractRevision, setContractRevision] = useState('');
  const [spellingEnabled, setSpellingEnabled] = useState(false);
  const onContractLoaded = useCallback((revision: string, enabled: boolean) => {
    setContractRevision(revision);
    setSpellingEnabled(enabled);
  }, []);
  const [bulkImportText, setBulkImportText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBook, setShowBook] = useState(true);
  const [showSeries, setShowSeries] = useState(true);
  const [sortState, setSortState] = useState<SortState>({ field: null, direction: null });
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [scopeTransferBusy, setScopeTransferBusy] = useState(false);

  const getSeriesEntries = (lists: Record<string, string[]>): string[] => {
    return resolveSeriesTerms(lists, targetLabel, currentLanguagePair);
  };

  const currentPairs = isAlways ? resolveBookTerms(alwaysTranslateWords, currentLanguagePair) : [];
  const seriesPairs = isAlways ? getSeriesEntries(seriesAlwaysTranslateWords) : [];
  const currentTerms = !isAlways ? resolveBookTerms(neverTranslateWords, currentLanguagePair) : [];
  const seriesTerms = !isAlways ? getSeriesEntries(seriesNeverTranslateWords) : [];
  const conflictNeverTerms = isAlways ? (conflictNeverTranslateWords[currentLanguagePair] || []) : [];
  const conflictSeriesNeverTerms = isAlways
    ? (getSeriesEntries(conflictSeriesNeverTranslateWords) || [])
    : [];
  const conflictAlwaysPairs = !isAlways ? (conflictAlwaysTranslateWords[currentLanguagePair] || []) : [];
  const conflictSeriesAlwaysPairs = !isAlways
    ? (getSeriesEntries(conflictSeriesAlwaysTranslateWords) || [])
    : [];

  useEffect(() => {
    if (isVisible) {
      setNewSourceWord('');
      setNewTargetWord('');
      setEditingIndex(null);
      setEditedSourceWord('');
      setEditedTargetWord('');
      setNewWord('');
      setEditingWord(null);
      setEditedWord('');
      setBulkImportText('');
      setSearchTerm('');
      setShowBook(true);
      setShowSeries(true);
      setSortState({ field: null, direction: null });
      setShowBulkImport(false);
    }
  }, [isVisible]);

  const updateAPI = async (words: string[]): Promise<boolean> => {
    if (tutorialModeRequested) {
      return true;
    }
    setIsLoading(true);
    try {
      const endpoint =
        ruleType === 'always'
          ? `update-always-translate/${bookGuid}`
          : `update-never-translate/${bookGuid}`;
      const response = await fetch(`${API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getAuthHeaders()),
        },
        body: JSON.stringify({
          language_pair: currentLanguagePair,
          words: words,
        }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error?.detail?.message || error?.detail || 'Failed to update glossary rules');
      }
      setContractRefresh(n => n + 1);
      props.onRefreshSeriesGlossary?.();
      return true;
    } catch (error) {
      console.error('Error updating API:', error);
      message.error(error instanceof Error ? error.message : 'Failed to update. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const isValidInput = isValidGlossaryTerm;

  const normalizeTerm = (input: string): string => input.trim().toLocaleLowerCase();
  const areTermsEquivalent = (a: string, b: string): boolean => normalizeTerm(a) === normalizeTerm(b);
  const getPairKey = (source: string, target: string) => `${normalizeTerm(source)}|${normalizeTerm(target)}`;
  const splitPair = (pair: string) => {
    const [rawSource = '', rawTarget = ''] = pair.split('|');
    return {
      source: rawSource.trim(),
      target: rawTarget.trim(),
    };
  };
  const splitTerms = (value: string) =>
    value
      .split(/\n|,|\|/)
      .map((entry) => entry.trim())
      .filter(Boolean);

  const seriesSourceSet = useMemo(() => {
    if (!isAlways) return new Set<string>();
    return new Set(
      seriesPairs
        .map((pair) => normalizeTerm(splitPair(pair).source))
        .filter(Boolean)
    );
  }, [isAlways, seriesPairs]);

  const seriesTermSet = useMemo(() => {
    if (isAlways) return new Set<string>();
    return new Set(seriesTerms.map((term) => normalizeTerm(term)).filter(Boolean));
  }, [isAlways, seriesTerms]);

  const conflictNeverSet = useMemo(() => {
    if (!isAlways) return new Set<string>();
    return new Set(
      [...conflictNeverTerms, ...conflictSeriesNeverTerms]
        .map((term) => normalizeTerm(term))
        .filter(Boolean)
    );
  }, [isAlways, conflictNeverTerms, conflictSeriesNeverTerms]);

  const conflictAlwaysSourceSet = useMemo(() => {
    if (isAlways) return new Set<string>();
    return new Set(
      [...conflictAlwaysPairs, ...conflictSeriesAlwaysPairs]
        .map((pair) => normalizeTerm(splitPair(pair).source))
        .filter(Boolean)
    );
  }, [isAlways, conflictAlwaysPairs, conflictSeriesAlwaysPairs]);

  const bookPairEntries = useMemo<GlossaryPairEntry[]>(
    () =>
      currentPairs
        .map((pair, index) => {
          const { source, target } = splitPair(pair);
          return {
            source,
            target,
            scope: 'book' as const,
            bookIndex: index,
            raw: pair,
          };
        }),
    [currentPairs, seriesSourceSet]
  );

  const seriesPairEntries = useMemo<GlossaryPairEntry[]>(
    () =>
      seriesPairs.map((pair) => {
        const { source, target } = splitPair(pair);
        return {
          source,
          target,
          scope: 'series',
          raw: pair,
        };
      }),
    [seriesPairs]
  );

  const bookTermEntries = useMemo<GlossaryTermEntry[]>(
    () =>
      currentTerms
        .map((term) => ({
          value: term,
          scope: 'book' as const,
          raw: term,
        })),
    [currentTerms, seriesTermSet]
  );

  const seriesTermEntries = useMemo<GlossaryTermEntry[]>(
    () =>
      seriesTerms.map((term) => ({
        value: term,
        scope: 'series',
        raw: term,
      })),
    [seriesTerms]
  );

  const totalCount = isAlways
    ? bookPairEntries.length + seriesPairEntries.length
    : bookTermEntries.length + seriesTermEntries.length;

  const filteredPairEntries = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const base = [
      ...(showBook ? bookPairEntries : []),
      ...(showSeries ? seriesPairEntries : []),
    ];
    const filtered = needle
      ? base.filter(
          (entry) =>
            entry.source.toLowerCase().includes(needle) ||
            entry.target.toLowerCase().includes(needle)
        )
      : base;
    if (!sortState.field || !sortState.direction) return filtered;
    const sortField = sortState.field;
    const sorted = [...filtered].sort((a, b) =>
      a[sortField].localeCompare(b[sortField], undefined, { sensitivity: 'base' })
    );
    return sortState.direction === 'desc' ? sorted.reverse() : sorted;
  }, [bookPairEntries, searchTerm, seriesPairEntries, showBook, showSeries, sortState]);

  const filteredTermEntries = useMemo(() => {
    const needle = searchTerm.trim().toLowerCase();
    const base = [
      ...(showBook ? bookTermEntries : []),
      ...(showSeries ? seriesTermEntries : []),
    ];
    const filtered = needle ? base.filter((entry) => entry.value.toLowerCase().includes(needle)) : base;
    if (!sortState.field || !sortState.direction) return filtered;
    if (sortState.field !== 'source') return filtered;
    const sorted = [...filtered].sort((a, b) =>
      a.value.localeCompare(b.value, undefined, { sensitivity: 'base' })
    );
    return sortState.direction === 'desc' ? sorted.reverse() : sorted;
  }, [bookTermEntries, searchTerm, seriesTermEntries, showBook, showSeries, sortState]);

  const filteredEntries = isAlways ? filteredPairEntries : filteredTermEntries;
  const showingStart = filteredEntries.length ? 1 : 0;
  const showingEnd = filteredEntries.length;
  const showingLabel = `Showing ${showingStart}-${showingEnd} of ${totalCount}`;
  const visibleBookCount = isAlways
    ? filteredPairEntries.filter((entry) => entry.scope === 'book').length
    : filteredTermEntries.filter((entry) => entry.scope === 'book').length;
  const visibleSeriesCount = isAlways
    ? filteredPairEntries.filter((entry) => entry.scope === 'series').length
    : filteredTermEntries.filter((entry) => entry.scope === 'series').length;
  const canTransferScope = Boolean(
    resolvedUserId && selectedSeriesId && sourceLang && targetLang && currentLanguagePair !== 'original'
    && (!props.seriesGlossaryStatus || props.seriesGlossaryStatus === 'loaded')
  );

  const updateLocalRulesFromTransfer = (
    payload: any,
    activeRuleType: RuleType
  ) => {
    if (activeRuleType === 'always' && setAlwaysTranslateWords) {
      const mapped = normalizeBookTermLists(payload?.bookTerms?.alwaysTranslate || {});
      setAlwaysTranslateWords((prevState) => ({ ...prevState, ...mapped }));
    }
    if (activeRuleType === 'never' && setNeverTranslateWords) {
      const mapped = normalizeBookTermLists(payload?.bookTerms?.neverTranslate || {});
      setNeverTranslateWords((prevState) => ({ ...prevState, ...mapped }));
    }
    if (setSeriesTranslateRules && payload?.seriesTerms) {
      setSeriesTranslateRules((prevState) => ({
        alwaysTranslate: {
          ...prevState.alwaysTranslate,
          ...(payload.seriesTerms.alwaysTranslate || {}),
        },
        neverTranslate: {
          ...prevState.neverTranslate,
          ...(payload.seriesTerms.neverTranslate || {}),
        },
      }));
    }
  };

  const executeScopeTransfer = async (direction: 'book_to_series' | 'series_to_book') => {
    if (!canTransferScope || !selectedSeriesId || !resolvedUserId) {
      message.warning('Attach this book to a series to move glossary scope.');
      return;
    }
    if (!sourceLang || !targetLang) {
      message.error('Select a valid language pair before moving glossary scope.');
      return;
    }

    const entriesToMove = isAlways
      ? (direction === 'book_to_series'
          ? filteredPairEntries.filter((entry) => entry.scope === 'book')
          : filteredPairEntries.filter((entry) => entry.scope === 'series')
        ).map((entry) => ({
          sourceTerm: entry.source,
          targetText: entry.target,
        }))
      : (direction === 'book_to_series'
          ? filteredTermEntries.filter((entry) => entry.scope === 'book')
          : filteredTermEntries.filter((entry) => entry.scope === 'series')
        ).map((entry) => ({
          sourceTerm: entry.value,
        }));

    if (!entriesToMove.length) {
      message.info('No matching terms in the current filters to move.');
      return;
    }

    const sourceLabelForAction = direction === 'book_to_series' ? 'Book' : 'Series';
    const targetLabelForAction = direction === 'book_to_series' ? 'Series' : 'Book';
    const confirmed = await new Promise<boolean>((resolve) => {
      const confirmFn = (Modal as any).confirm;
      if (typeof confirmFn !== 'function') {
        resolve(true);
        return;
      }
      confirmFn({
        rootClassName: 'theme-aware-confirm',
        title: `Move ${entriesToMove.length} ${isAlways ? 'pair' : 'term'}${entriesToMove.length > 1 ? 's' : ''}?`,
        content: `Move from ${sourceLabelForAction} to ${targetLabelForAction} for ${sourceLang} -> ${targetLang}.`,
        okText: 'Move',
        cancelText: 'Cancel',
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    setScopeTransferBusy(true);
    try {
      const response = await fetch(`${API_BASE}/books/${bookGuid}/glossary/scope-transfer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: resolvedUserId,
          sourceLang,
          targetLang,
          ruleType: ruleType,
          direction,
          removeFromSource: true,
          items: entriesToMove,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || 'Failed to transfer glossary scope.');
      }
      updateLocalRulesFromTransfer(payload, ruleType);
      const movedCount = Number(payload?.moved || 0);
      const skipped = Array.isArray(payload?.skipped) ? payload.skipped : [];
      message.success(`Moved ${movedCount} ${isAlways ? 'pair' : 'term'}${movedCount === 1 ? '' : 's'}.`);
      if (skipped.length) {
        const preview = skipped
          .slice(0, 3)
          .map((entry: any) => `${entry?.sourceTerm || 'Entry'} (${entry?.reason || 'skipped'})`)
          .join('; ');
        const suffix = skipped.length > 3 ? ' …' : '';
        message.warning(`Skipped ${skipped.length}: ${preview}${suffix}`);
      }
    } catch (error: any) {
      console.error('Failed glossary scope transfer', error);
      message.error(error?.message || 'Failed to transfer glossary scope.');
    } finally {
      setScopeTransferBusy(false);
    }
  };

  const handleSortToggle = (field: SortField) => {
    setSortState((prev) => {
      if (prev.field !== field) {
        return { field, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { field, direction: 'desc' };
      }
      if (prev.direction === 'desc') {
        return { field: null, direction: null };
      }
      return { field, direction: 'asc' };
    });
  };

  const getSortIndicator = (field: SortField) => {
    if (sortState.field !== field || !sortState.direction) return '';
    return sortState.direction === 'asc' ? ' (A-Z)' : ' (Z-A)';
  };

  const handleAddPair = async () => {
    if (!isAlways || !setAlwaysTranslateWords) return;
    const trimmedSource = newSourceWord.trim();
    const trimmedTarget = newTargetWord.trim();

    if (trimmedSource && trimmedTarget) {
      if (spellingEnabled && trimmedSource !== trimmedTarget && areTermsEquivalent(trimmedSource, trimmedTarget)) {
        setIsLoading(true);
        try {
          const response = await bookDetailApiPost(`${API_BASE}/books/${bookGuid}/glossary-spelling-intent`, {
            source_lang: sourceLang, target_lang: targetLang, source: trimmedSource, target: trimmedTarget,
            scope: 'book', expected_revision: contractRevision,
          });
          const words = response.data.rules.filter((row: any) => row.scope === 'book' && row.kind === 'always').map((row: any) => row.raw);
          setAlwaysTranslateWords(previous => ({ ...previous, [currentLanguagePair]: words }));
          setContractRefresh(value => value + 1);
          setNewSourceWord('');
          setNewTargetWord('');
        } catch (error: any) {
          message.error(typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'The spelling decision could not be saved. Refresh and try again.');
        } finally {
          setIsLoading(false);
        }
        return;
      }
      if (areTermsEquivalent(trimmedSource, trimmedTarget)) {
        message.error(
          'Source and target must be different. Add identical terms to the Never Translate glossary instead.'
        );
        return;
      }

      const normalizedSource = normalizeTerm(trimmedSource);
      if (seriesSourceSet.has(normalizedSource)) {
        message.warning('That source term already exists in the series glossary.');
        return;
      }
      if (conflictNeverSet.has(normalizedSource)) {
        message.warning('That source term already exists in the Never Translate glossary.');
        return;
      }

      const existingPairs = currentPairs;
      const normalizedPairKey = getPairKey(trimmedSource, trimmedTarget);
      const alreadyExists = existingPairs.some((pair) => {
        const { source, target } = splitPair(pair);
        return getPairKey(source, target) === normalizedPairKey;
      });
      if (alreadyExists) {
        message.warning('That pair already exists.');
        return;
      }

      if (isValidInput(trimmedSource) && isValidInput(trimmedTarget)) {
        const newPair = `${trimmedSource} | ${trimmedTarget}`;
        const updatedWords = [...existingPairs, newPair];
        const success = await updateAPI(updatedWords);
        if (success) {
          setAlwaysTranslateWords((prevState) => ({
            ...prevState,
            [currentLanguagePair]: updatedWords,
          }));
          setNewSourceWord('');
          setNewTargetWord('');
        }
      } else {
        message.error(
          'Please enter valid words or phrases (letters, numbers, spaces, and common punctuation are allowed).'
        );
      }
    } else {
      message.error('Please enter both source and target words or phrases.');
    }
  };

  const handleSavePairEdit = async () => {
    if (!isAlways || !setAlwaysTranslateWords) return;
    if (editingIndex !== null) {
      const trimmedSource = editedSourceWord.trim();
      const trimmedTarget = editedTargetWord.trim();

      if (!trimmedSource || !trimmedTarget) {
        message.error('Please enter both source and target words or phrases.');
        return;
      }

      const previousParts = (currentPairs[editingIndex] || '').split('|').map(value => value.trim());
      if (trimmedSource === previousParts[0] && trimmedTarget === previousParts[1]) {
        setEditingIndex(null);
        return;
      }

      if (areTermsEquivalent(trimmedSource, trimmedTarget)) {
        message.error(
          'Source and target must be different. Add identical terms to the Never Translate glossary instead.'
        );
        return;
      }

      const normalizedSource = normalizeTerm(trimmedSource);
      if (seriesSourceSet.has(normalizedSource)) {
        message.warning('That source term already exists in the series glossary.');
        return;
      }
      if (conflictNeverSet.has(normalizedSource)) {
        message.warning('That source term already exists in the Never Translate glossary.');
        return;
      }

      if (isValidInput(trimmedSource) && isValidInput(trimmedTarget)) {
        const updatedWords = [...currentPairs];
        const normalizedPairKey = getPairKey(trimmedSource, trimmedTarget);
        const duplicateIndex = updatedWords.findIndex((pair, idx) => {
          if (idx === editingIndex) return false;
          const { source, target } = splitPair(pair);
          return getPairKey(source, target) === normalizedPairKey;
        });
        if (duplicateIndex !== -1) {
          message.warning('Another pair already has that source/target.');
          return;
        }

        updatedWords[editingIndex] = `${trimmedSource} | ${trimmedTarget}`;
        const success = await updateAPI(updatedWords);
        if (success) {
          setAlwaysTranslateWords((prevState) => ({
            ...prevState,
            [currentLanguagePair]: updatedWords,
          }));
          setEditingIndex(null);
        }
      } else {
        message.error(
          'Please enter valid words or phrases (letters, numbers, spaces, and common punctuation are allowed).'
        );
      }
    } else {
      message.error('Please enter both source and target words or phrases.');
    }
  };

  const handleRemovePair = async (index: number) => {
    if (!isAlways || !setAlwaysTranslateWords) return;
    const updatedWords = currentPairs.filter((_, i) => i !== index);
    const success = await updateAPI(updatedWords);
    if (success) {
      setAlwaysTranslateWords((prevState) => ({
        ...prevState,
        [currentLanguagePair]: updatedWords,
      }));
    }
  };

  const handleEditPair = (index: number) => {
    if (!isAlways) return;
    const pair = currentPairs[index];
    const { source, target } = splitPair(pair);
    setEditingIndex(index);
    setEditedSourceWord(source);
    setEditedTargetWord(target);
  };

  const handleCancelEdit = () => {
    if (isAlways) {
      setEditingIndex(null);
      setEditedSourceWord('');
      setEditedTargetWord('');
    } else {
      setEditingWord(null);
      setEditedWord('');
    }
  };

  const formatPair = (pair: string) => {
    const { source, target } = splitPair(pair);
    return `${source} ~ ${target}`;
  };

  const handleCopyPairs = async (format: 'readable' | 'bulk') => {
    if (!filteredPairEntries.length) {
      message.info('No pairs to copy yet.');
      return;
    }

    const formatted = filteredPairEntries.map((entry) => formatPair(entry.raw));
    const text = format === 'readable' ? formatted.join('\n') : formatted.join(' | ');

    try {
      await navigator.clipboard.writeText(text);
      message.success(format === 'readable' ? 'Copied human-readable list.' : 'Copied bulk import string.');
    } catch (err) {
      console.error('Clipboard copy failed', err);
      message.error('Unable to copy to clipboard.');
    }
  };

  const handleBulkPairImport = async () => {
    if (!isAlways || !setAlwaysTranslateWords) return;
    const trimmedInput = bulkImportText.trim();
    if (!trimmedInput) {
      message.error('Enter one or more pairs before importing.');
      return;
    }

    const entries = trimmedInput
      .split(/\n|\|/)
      .map((entry) => entry.trim())
      .filter(Boolean);

    if (!entries.length) {
      message.error('No valid pairs detected. Use the format "source ~ target | source2 ~ target2".');
      return;
    }

    const existingPairs = currentPairs;
    const existingKeys = new Set(
      existingPairs.map((pair) => {
        const { source, target } = splitPair(pair);
        return getPairKey(source, target);
      })
    );
    const seriesKeys = new Set(
      seriesPairs.map((pair) => {
        const { source, target } = splitPair(pair);
        return getPairKey(source, target);
      })
    );

    const pairsToAdd: string[] = [];
    const invalidEntries: Array<{ entry: string; reason: string }> = [];
    const duplicateEntries: string[] = [];

    entries.forEach((entry) => {
      const [rawSource = '', rawTarget = ''] = entry.split('~');
      const source = rawSource.trim();
      const target = rawTarget.trim();

      if (!source || !target) {
        invalidEntries.push({ entry, reason: 'Missing source or target' });
        return;
      }

      if (areTermsEquivalent(source, target)) {
        invalidEntries.push({ entry, reason: 'Source and target are the same' });
        return;
      }

      if (!isValidInput(source) || !isValidInput(target)) {
        invalidEntries.push({ entry, reason: 'Contains unsupported characters' });
        return;
      }

      const normalizedSource = normalizeTerm(source);
      if (seriesSourceSet.has(normalizedSource)) {
        invalidEntries.push({ entry, reason: 'Source exists in series glossary' });
        return;
      }
      if (conflictNeverSet.has(normalizedSource)) {
        invalidEntries.push({ entry, reason: 'Source conflicts with Never Translate glossary' });
        return;
      }

      const pairKey = getPairKey(source, target);
      if (existingKeys.has(pairKey) || seriesKeys.has(pairKey)) {
        duplicateEntries.push(entry);
        return;
      }

      existingKeys.add(pairKey);
      pairsToAdd.push(`${source} | ${target}`);
    });

    if (!pairsToAdd.length) {
      message.warning('No new valid pairs to import.');
      return;
    }

    const updatedWords = [...existingPairs, ...pairsToAdd];
    const success = await updateAPI(updatedWords);
    if (success) {
      setAlwaysTranslateWords((prevState) => ({
        ...prevState,
        [currentLanguagePair]: updatedWords,
      }));
      setBulkImportText('');
      message.success(`Imported ${pairsToAdd.length} new pair${pairsToAdd.length > 1 ? 's' : ''}.`);
      if (invalidEntries.length) {
        const details = invalidEntries
          .map(({ entry, reason }) => `"${entry}" (${reason})`)
          .join('; ');
        message.warning(
          `Skipped ${invalidEntries.length} invalid entr${invalidEntries.length > 1 ? 'ies' : 'y'}: ${details}`
        );
      }
      if (duplicateEntries.length) {
        const details = duplicateEntries.map((entry) => `"${entry}"`).join('; ');
        message.info(
          `Ignored ${duplicateEntries.length} duplicate entr${duplicateEntries.length > 1 ? 'ies' : 'y'}: ${details}`
        );
      }
    }
  };

  const applyTermImport = async (input: string, emptyMessage: string, clearInput: () => void) => {
    if (isAlways || !setNeverTranslateWords) return;
    const entries = splitTerms(input);
    if (!entries.length) {
      message.error(emptyMessage);
      return;
    }

    const existingWords = currentTerms;
    const existingKeys = new Set(existingWords.map((word) => normalizeTerm(word)));
    const seriesKeys = new Set(seriesTerms.map((word) => normalizeTerm(word)));
    const validEntries: string[] = [];
    const invalidEntries: Array<{ entry: string; reason: string }> = [];
    const duplicateEntries: string[] = [];

    entries.forEach((entry) => {
      if (!isValidInput(entry)) {
        invalidEntries.push({ entry, reason: 'Contains unsupported characters' });
        return;
      }
      const key = normalizeTerm(entry);
      if (conflictAlwaysSourceSet.has(key)) {
        invalidEntries.push({ entry, reason: 'Conflicts with Always Translate glossary' });
        return;
      }
      if (existingKeys.has(key) || seriesKeys.has(key)) {
        duplicateEntries.push(entry);
        return;
      }
      existingKeys.add(key);
      validEntries.push(entry);
    });

    if (!validEntries.length) {
      message.warning('No new valid text to add.');
      return;
    }

    const updatedWords = [...existingWords, ...validEntries];
    const success = await updateAPI(updatedWords);
    if (success) {
      setNeverTranslateWords((prev) => ({
        ...prev,
        [currentLanguagePair]: updatedWords,
      }));
      clearInput();
      message.success(
        `Added ${validEntries.length} new entr${validEntries.length > 1 ? 'ies' : 'y'}.`
      );
      if (invalidEntries.length) {
        const details = invalidEntries
          .map(({ entry, reason }) => `"${entry}" (${reason})`)
          .join('; ');
        message.warning(
          `Skipped ${invalidEntries.length} invalid entr${invalidEntries.length > 1 ? 'ies' : 'y'}: ${details}`
        );
      }
      if (duplicateEntries.length) {
        message.info(
          `Ignored ${duplicateEntries.length} duplicate entr${
            duplicateEntries.length > 1 ? 'ies' : 'y'
          }: ${duplicateEntries.join('; ')}`
        );
      }
    }
  };

  const handleAddTerms = async () =>
    applyTermImport(newWord, 'Enter at least one word or phrase to add.', () => setNewWord(''));

  const handleBulkTermImport = async () =>
    applyTermImport(bulkImportText, 'Enter one or more terms before importing.', () => setBulkImportText(''));

  const handleSaveTermEdit = async () => {
    if (isAlways || !setNeverTranslateWords) return;
    if (!editingWord) {
      message.error('Please select a word to edit.');
      return;
    }
    const trimmed = editedWord.trim();
    if (!trimmed) {
      message.error('Please enter a valid word or phrase.');
      return;
    }
    if (!isValidInput(trimmed)) {
      message.error(
        'Please enter a valid word or phrase (letters, numbers, spaces, and common punctuation are allowed).'
      );
      return;
    }

    const existingWords = currentTerms;
    const key = normalizeTerm(trimmed);
    if (conflictAlwaysSourceSet.has(key)) {
      message.warning('That term already exists in the Always Translate glossary.');
      return;
    }
    const seriesKeys = new Set(seriesTerms.map((word) => normalizeTerm(word)));
    if (seriesKeys.has(key)) {
      message.warning('That term already exists in the series glossary.');
      return;
    }
    const duplicate = existingWords.some((word) => word !== editingWord && normalizeTerm(word) === key);
    if (duplicate) {
      message.warning('That word or phrase already exists.');
      return;
    }

    const updatedWords = existingWords.map((word) => (word === editingWord ? trimmed : word));
    const success = await updateAPI(updatedWords);
    if (success) {
      setNeverTranslateWords((prev) => ({
        ...prev,
        [currentLanguagePair]: updatedWords,
      }));
      setEditingWord(null);
    }
  };

  const handleRemoveTerm = async (word: string) => {
    if (isAlways || !setNeverTranslateWords) return;
    const updatedWords = currentTerms.filter((w) => w !== word);
    const success = await updateAPI(updatedWords);
    if (success) {
      setNeverTranslateWords((prev) => ({
        ...prev,
        [currentLanguagePair]: updatedWords,
      }));
    }
  };

  const handleEditTerm = (oldWord: string) => {
    if (isAlways) return;
    setEditingWord(oldWord);
    setEditedWord(oldWord);
  };

  const handleCopyTerms = async (format: 'readable' | 'pipe') => {
    if (!filteredTermEntries.length) {
      message.info('No terms to copy yet.');
      return;
    }
    const values = filteredTermEntries.map((entry) => entry.value);
    const text = format === 'readable' ? values.join('\n') : values.map((term) => term.trim()).join(' | ');
    try {
      await navigator.clipboard.writeText(text);
      message.success(
        format === 'readable' ? 'Copied list to clipboard.' : 'Copied import string to clipboard.'
      );
    } catch (err) {
      console.error('Clipboard copy failed', err);
      message.error('Unable to copy to clipboard.');
    }
  };

  const handleModalClose = () => {
    if (isAlways) {
      setEditingIndex(null);
    } else {
      setNewWord('');
      setEditingWord(null);
      setEditedWord('');
    }
    onClose();
  };

  const languageLabel = targetLabel ? `${sourceLabel} → ${targetLabel}` : currentLanguagePair;
  const ruleLabel = isAlways ? 'Always translate' : 'Never translate';
  const headerHelpText = isAlways
    ? 'The glossary will always translate the source text to the specified target text'
    : 'The glossary will be instructed to never translate the text entered here';
  const nextRuleType: RuleType = isAlways ? 'never' : 'always';
  const switchLabel = isAlways ? 'Switch to Never translate' : 'Switch to Always translate';
  const isBusy = isLoading || scopeTransferBusy;
  const loadingStatusMessage = scopeTransferBusy
    ? `Moving visible ${isAlways ? 'pairs' : 'terms'} between Book and Series scopes...`
    : `Saving ${isAlways ? 'glossary pairs' : 'glossary terms'}...`;

  const tableColumns = 'minmax(160px, 1.2fr) minmax(160px, 1.2fr) 110px 90px';
  const bookTagStyle = {
    color: 'var(--text-secondary)',
    borderColor: 'var(--border-subtle)',
    background: 'var(--surface-muted)',
  };
  const seriesTagStyle = isAlways
    ? { color: 'var(--text-info)', borderColor: 'var(--border-info)', background: 'var(--surface-info)' }
    : { color: 'var(--text-danger)', borderColor: 'var(--border-danger)', background: 'var(--surface-danger)' };

  const listHelpText = isAlways
    ? 'We will direct the AI to always translate these source terms to their specified target equivalents.'
    : 'We will direct the AI to never translate these words or phrases.';

  const emptyStateText = totalCount
    ? isAlways
      ? 'No glossary pairs match the current filters.'
      : 'No glossary terms match the current filters.'
    : isAlways
      ? 'No glossary pairs added yet. Add your first pair above.'
      : 'No glossary terms added yet. Add your first term above.';
  const copyDisabled = !filteredEntries.length;
  const copyMenu = (
    <Menu>
      <Menu.Item
        key="copy-list"
        onClick={() => (isAlways ? handleCopyPairs('readable') : handleCopyTerms('readable'))}
        disabled={copyDisabled}
      >
        Copy visible list
      </Menu.Item>
      <Menu.Item
        key="copy-import"
        onClick={() => (isAlways ? handleCopyPairs('bulk') : handleCopyTerms('pipe'))}
        disabled={copyDisabled}
      >
        Copy visible import string
      </Menu.Item>
    </Menu>
  );
  const tutorialTestIdProps = {
    alwaysModal: { 'data-testid': 'tutorial:translation-rules:always-modal' } as const,
    neverModal: { 'data-testid': 'tutorial:translation-rules:never-modal' } as const,
    alwaysClose: { 'data-testid': 'tutorial:translation-rules:always-close' } as const,
    neverClose: { 'data-testid': 'tutorial:translation-rules:never-close' } as const,
    alwaysAdd: { 'data-testid': 'tutorial:translation-rules:always-add' } as const,
    neverAdd: { 'data-testid': 'tutorial:translation-rules:never-add' } as const,
  };
  const modalTestIdProps = isAlways ? tutorialTestIdProps.alwaysModal : tutorialTestIdProps.neverModal;
  const closeButtonTestIdProps = isAlways ? tutorialTestIdProps.alwaysClose : tutorialTestIdProps.neverClose;
  const addButtonTestIdProps = isAlways ? tutorialTestIdProps.alwaysAdd : tutorialTestIdProps.neverAdd;
  const footerSummary = `${filteredEntries.length} visible of ${totalCount} ${isAlways ? 'pairs' : 'terms'} - Book ${isAlways ? bookPairEntries.length : bookTermEntries.length} - Series ${isAlways ? seriesPairs.length : seriesTerms.length}`;

  return (
    <Modal
      title={
        <span className="glossary-rule-title">
          <span>{`Glossary • ${ruleLabel} • ${languageLabel}`}</span>
          <Tooltip title={headerHelpText}>
            <QuestionCircleOutlined style={{ cursor: 'pointer', color: 'var(--text-info)' }} />
          </Tooltip>
        </span>
      }
      open={isVisible}
      onCancel={handleModalClose}
      closable={!isBusy}
      maskClosable={!isBusy}
      keyboard={!isBusy}
      className="glossary-rule-modal"
      footer={
        <div className="glossary-rule-footer">
          <div className="glossary-rule-footer__summary" aria-live="polite">
            <strong>{isBusy ? 'Saving changes' : `${ruleLabel} rules`}</strong>
            <span>{isBusy ? loadingStatusMessage : footerSummary}</span>
          </div>
          <div>
            {onSwitchRuleType && (
              <Button type="link" size="small" onClick={() => onSwitchRuleType(nextRuleType)} disabled={isBusy}>
                {switchLabel}
              </Button>
            )}
            <Button
              onClick={handleModalClose}
              disabled={isBusy}
              {...closeButtonTestIdProps}
            >
              Close
            </Button>
          </div>
        </div>
      }
      width={820}
      style={{ top: 24 }}
      bodyStyle={{
        maxHeight: 'min(calc(100dvh - 176px), 780px)',
        overflowX: 'hidden',
        overflowY: 'auto',
      }}
    >
      <div className="glossary-rule-body" {...modalTestIdProps}>
        {isVisible && props.onRefreshSeriesGlossary && !tutorialModeRequested && (
          <ResolvedGlossaryPanel bookId={bookGuid} sourceLang={sourceLang} targetLang={targetLang}
            onContractLoaded={onContractLoaded}
            refreshKey={`${contractRefresh}:${props.seriesGlossaryStatus}`} />
        )}
        {props.seriesGlossaryStatus === 'error' && (
          <div role="alert">
            Series glossary could not be loaded. Any displayed series entries are an older copy.
            <Button onClick={props.onRefreshSeriesGlossary}>Retry</Button>
          </div>
        )}
        {props.seriesGlossaryStatus === 'loading' && (
          <div role="status">Refreshing series glossary. Any displayed series entries are an older copy.</div>
        )}
        <div
          style={
            isBusy
              ? {
                  opacity: 0.68,
                }
              : undefined
          }
        >
        <section
          className="glossary-rule-block glossary-rule-block--add"
          style={{
            marginBottom: '15px',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              marginBottom: 12,
            }}
          >
            <strong>Add items</strong>
            <Button size="small" onClick={() => setShowBulkImport((prev) => !prev)}>
              {showBulkImport ? 'Hide bulk import' : 'Show bulk import'}
            </Button>
          </div>

          {isAlways ? (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                gap: 12,
                marginBottom: 15,
              }}
            >
              <div>
                <label style={{ marginRight: '10px', display: 'block', marginBottom: '5px' }}>
                  <strong>Source term:</strong>
                </label>
                <Input
                  value={newSourceWord}
                  onChange={(e) => setNewSourceWord(e.target.value)}
                  placeholder="Enter source term"
                  style={{ width: '100%' }}
                  data-testid="tutorial:translation-rules:always-source-input"
                />
              </div>
              <div>
                <label style={{ marginRight: '10px', display: 'block', marginBottom: '5px' }}>
                  <strong>Target term:</strong>
                </label>
                <Input
                  value={newTargetWord}
                  onChange={(e) => setNewTargetWord(e.target.value)}
                  placeholder="Enter target term"
                  style={{ width: '100%' }}
                  data-testid="tutorial:translation-rules:always-target-input"
                />
              </div>
            </div>
          ) : (
            <div style={{ marginBottom: 15 }}>
              <label style={{ marginRight: '10px', display: 'block', marginBottom: '5px' }}>
                <strong>Glossary term(s) to keep in source:</strong>
              </label>
              <Input.TextArea
                rows={2}
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder="Enter words or phrases to keep"
                style={{ width: '100%' }}
                data-testid="tutorial:translation-rules:never-input"
              />
              <div style={{ marginTop: 6, color: 'var(--text-secondary)' }}>
                Separate entries with commas, pipes, or new lines (example: <em>Hero, Sidekick, Location</em>).
              </div>
            </div>
          )}

          <Button
            type="primary"
            onClick={isAlways ? handleAddPair : handleAddTerms}
            icon={<PlusOutlined />}
            {...addButtonTestIdProps}
          >
            {isAlways ? spellingEnabled && newSourceWord.trim() !== newTargetWord.trim() && areTermsEquivalent(newSourceWord, newTargetWord) ? 'Use this spelling' : 'Add glossary pair' : 'Add glossary terms'}
          </Button>

          {showBulkImport && (
            <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--border-subtle)' }}>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 8 }}>
                {isAlways ? (
                  <>
                    Use the format <code>source ~ target | source2 ~ target2</code> or separate pairs with new lines.
                  </>
                ) : (
                  <>Separate entries with commas, pipes, or new lines.</>
                )}
              </div>
              <Input.TextArea
                rows={3}
                value={bulkImportText}
                onChange={(e) => setBulkImportText(e.target.value)}
                placeholder={
                  isAlways ? 'e.g., Coffee ~ Kopi | Tea ~ Teh' : 'e.g., Hero, Sidekick\nLocation'
                }
              />
              <Button
                style={{ marginTop: 10 }}
                onClick={isAlways ? handleBulkPairImport : handleBulkTermImport}
                disabled={!bulkImportText.trim()}
              >
                {isAlways ? 'Import Pairs' : 'Import Terms'}
              </Button>
            </div>
          )}
        </section>

        <section
          className="glossary-rule-block glossary-rule-block--list"
        >
          <div
            style={{
              marginBottom: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <strong>{isAlways ? 'Glossary pairs' : 'Glossary terms'}</strong>
              <Tooltip title={listHelpText}>
                <QuestionCircleOutlined style={{ color: 'var(--text-info)' }} />
              </Tooltip>
              <Dropdown overlay={copyMenu} trigger={['click']}>
                <Button
                  size="small"
                  type="text"
                  icon={<CopyOutlined />}
                  disabled={copyDisabled}
                  aria-label="Copy options"
                />
              </Dropdown>
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={isAlways ? 'Search glossary pairs' : 'Search glossary terms'}
              style={{ width: 280, maxWidth: '100%' }}
              allowClear
            />
            <span style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{showingLabel}</span>
          </div>

          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 12,
              flexWrap: 'wrap',
              marginBottom: 12,
              marginTop: 10,
            }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>Visible scopes</span>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                <Tooltip title="Toggle Book-scope entries in the list below.">
                  <Button
                    size="small"
                    type={showBook ? 'primary' : 'default'}
                    onClick={() => setShowBook((prev) => !prev)}
                    aria-pressed={showBook}
                  >
                    Book ({isAlways ? bookPairEntries.length : bookTermEntries.length})
                  </Button>
                </Tooltip>
                <Tooltip title="Toggle Series-scope entries in the list below.">
                  <Button
                    size="small"
                    type={showSeries ? 'primary' : 'default'}
                    onClick={() => setShowSeries((prev) => !prev)}
                    aria-pressed={showSeries}
                  >
                    Series ({props.seriesGlossaryStatus === 'error' ? 'unavailable' : props.seriesGlossaryStatus === 'loading' ? 'refreshing' : isAlways ? seriesPairs.length : seriesTerms.length})
                  </Button>
                </Tooltip>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginLeft: 'auto' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600 }}>
                Bulk move visible entries
              </span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <Tooltip
                  title={
                    canTransferScope
                      ? `Move ${visibleBookCount} visible Book entr${visibleBookCount === 1 ? 'y' : 'ies'} to Series (respects current search/scope filters).`
                      : 'Attach this book to a series to move scope.'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      data-testid={
                        isAlways
                          ? 'tutorial:translation-rules:always-move-book-to-series'
                          : 'tutorial:translation-rules:never-move-book-to-series'
                      }
                      onClick={() => {
                        void executeScopeTransfer('book_to_series');
                      }}
                      disabled={!canTransferScope || !visibleBookCount || isLoading || scopeTransferBusy}
                    >
                      Move Book -&gt; Series ({visibleBookCount})
                    </Button>
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    canTransferScope
                      ? `Move ${visibleSeriesCount} visible Series entr${visibleSeriesCount === 1 ? 'y' : 'ies'} to Book (respects current search/scope filters).`
                      : 'Attach this book to a series to move scope.'
                  }
                >
                  <span>
                    <Button
                      size="small"
                      data-testid={
                        isAlways
                          ? 'tutorial:translation-rules:always-move-series-to-book'
                          : 'tutorial:translation-rules:never-move-series-to-book'
                      }
                      onClick={() => {
                        void executeScopeTransfer('series_to_book');
                      }}
                      disabled={!canTransferScope || !visibleSeriesCount || isLoading || scopeTransferBusy}
                    >
                      Move Series -&gt; Book ({visibleSeriesCount})
                    </Button>
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>

          <div
            style={{
              border: '1px solid var(--border-subtle)',
              borderRadius: 6,
              background: 'var(--surface-base)',
              overflow: 'hidden',
            }}
            data-testid={isAlways ? 'tutorial:translation-rules:always-list' : 'tutorial:translation-rules:never-list'}
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: tableColumns,
                gap: 12,
                padding: '8px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                background: 'var(--surface-muted)',
                borderBottom: '1px solid var(--border-subtle)',
              }}
            >
              {isAlways ? (
                <>
                  <button
                    type="button"
                    onClick={() => handleSortToggle('source')}
                    title="Sort by source"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Source{getSortIndicator('source')}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSortToggle('target')}
                    title="Sort by target"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Target{getSortIndicator('target')}
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleSortToggle('source')}
                    title="Sort by term"
                    style={{
                      background: 'none',
                      border: 'none',
                      padding: 0,
                      font: 'inherit',
                      color: 'inherit',
                      textAlign: 'left',
                      cursor: 'pointer',
                    }}
                  >
                    Term{getSortIndicator('source')}
                  </button>
                  <span style={{ textAlign: 'left' }}>Target</span>
                </>
              )}
              <span style={{ textAlign: 'left' }}>Scope</span>
              <span style={{ textAlign: 'right' }}>Actions</span>
            </div>
            <div className="glossary-rule-list-scroll">
              {filteredEntries.length ? (
                filteredEntries.map((entry, rowIndex) => {
                  if (isAlways) {
                    const pairEntry = entry as GlossaryPairEntry;
                    const isEditing = pairEntry.scope === 'book' && pairEntry.bookIndex === editingIndex;
                    return (
                      <div
                        key={`${pairEntry.scope}-${pairEntry.raw}-${rowIndex}`}
                        style={{
                          display: 'grid',
                          gridTemplateColumns: tableColumns,
                          gap: 12,
                          padding: '8px 12px',
                          alignItems: 'center',
                          borderBottom: '1px solid var(--border-subtle)',
                        }}
                      >
                        {isEditing ? (
                          <Input
                            value={editedSourceWord}
                            onChange={(e) => setEditedSourceWord(e.target.value)}
                          />
                        ) : (
                          <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>
                            {pairEntry.source}
                          </span>
                        )}
                        {isEditing ? (
                          <Input
                            value={editedTargetWord}
                            onChange={(e) => setEditedTargetWord(e.target.value)}
                          />
                        ) : (
                          <span style={{ overflowWrap: 'anywhere' }}>
                            {pairEntry.target}
                            {pairEntry.scope === 'book' && seriesSourceSet.has(normalizeTerm(pairEntry.source)) && (
                              <small> — Saved in Book; Series has a rule for this source.</small>
                            )}
                            {pairEntry.source !== pairEntry.target && areTermsEquivalent(pairEntry.source, pairEntry.target) && (
                              <small> — Capitalization rule: check its status in the effective glossary.</small>
                            )}
                          </span>
                        )}
                        <Tag style={pairEntry.scope === 'series' ? seriesTagStyle : bookTagStyle}>
                          {pairEntry.scope === 'series' ? 'Series' : 'Book'}
                        </Tag>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                          {pairEntry.scope === 'book' ? (
                            isEditing ? (
                              <>
                                <Tooltip title="Save">
                                  <SaveOutlined
                                    onClick={handleSavePairEdit}
                                    style={{ cursor: 'pointer', color: 'var(--text-info)' }}
                                  />
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <CloseOutlined
                                    onClick={handleCancelEdit}
                                    style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
                                  />
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Edit">
                                  <EditOutlined
                                    onClick={() => handleEditPair(pairEntry.bookIndex ?? 0)}
                                    style={{ cursor: 'pointer', color: 'var(--text-info)' }}
                                    data-testid="tutorial:translation-rules:always-edit"
                                  />
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <DeleteOutlined
                                    onClick={() => handleRemovePair(pairEntry.bookIndex ?? 0)}
                                    style={{ cursor: 'pointer', color: 'var(--text-danger)' }}
                                    data-testid="tutorial:translation-rules:always-delete"
                                  />
                                </Tooltip>
                              </>
                            )
                          ) : (
                            <span style={{ color: 'var(--text-secondary)' }}>Read only</span>
                          )}
                        </div>
                      </div>
                    );
                  }

                  const termEntry = entry as GlossaryTermEntry;
                  const isEditing = termEntry.scope === 'book' && termEntry.value === editingWord;
                  return (
                    <div
                      key={`${termEntry.scope}-${termEntry.raw}-${rowIndex}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: tableColumns,
                        gap: 12,
                        padding: '8px 12px',
                        alignItems: 'center',
                        borderBottom: '1px solid var(--border-subtle)',
                      }}
                    >
                      {isEditing ? (
                        <Input value={editedWord} onChange={(e) => setEditedWord(e.target.value)} />
                      ) : (
                        <span style={{ fontWeight: 500, overflowWrap: 'anywhere' }}>{termEntry.value}</span>
                      )}
                      <span style={{ color: 'var(--text-secondary)' }}>—</span>
                      <Tag style={termEntry.scope === 'series' ? seriesTagStyle : bookTagStyle}>
                        {termEntry.scope === 'series' ? 'Series' : 'Book'}
                      </Tag>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                        {termEntry.scope === 'book' ? (
                          isEditing ? (
                            <>
                              <Tooltip title="Save">
                                <SaveOutlined
                                  onClick={handleSaveTermEdit}
                                  style={{ cursor: 'pointer', color: 'var(--text-info)' }}
                                />
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <CloseOutlined
                                  onClick={handleCancelEdit}
                                  style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
                                />
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <Tooltip title="Edit">
                                <EditOutlined
                                  onClick={() => handleEditTerm(termEntry.value)}
                                  style={{ cursor: 'pointer', color: 'var(--text-info)' }}
                                  data-testid="tutorial:translation-rules:never-edit"
                                />
                              </Tooltip>
                              <Tooltip title="Delete">
                                <DeleteOutlined
                                  onClick={() => handleRemoveTerm(termEntry.value)}
                                  style={{ cursor: 'pointer', color: 'var(--text-danger)' }}
                                  data-testid="tutorial:translation-rules:never-delete"
                                />
                              </Tooltip>
                            </>
                          )
                        ) : (
                          <span style={{ color: 'var(--text-secondary)' }}>Read only</span>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ color: 'var(--text-secondary)', padding: '12px' }}>{emptyStateText}</div>
              )}
            </div>
          </div>
        </section>
        </div>
        {isBusy && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 16,
              borderRadius: 8,
              background: 'var(--overlay-backdrop)',
            }}
          >
            <LoadingCard
              label={loadingStatusMessage}
              style={{
                minWidth: 280,
                maxWidth: 560,
                width: 'min(100%, 560px)',
              }}
            />
          </div>
        )}
      </div>
    </Modal>
  );
};

export default GlossaryRuleModal;
